/**
 * Version Checker Utility
 * 
 * This utility periodically checks if a new version of the app has been deployed.
 * When detecting a new version, it prompts users to refresh to get the latest updates.
 * 
 * This is especially important for financial applications where users need the latest
 * security patches and features.
 */

import logger from './logger.js';
import i18n from '@/app/config/i18n/config/i18n.js';

const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const APP_VERSION_KEY = 'app_version_timestamp';

class VersionChecker {
    constructor() {
        this.currentVersion = null;
        this.checkInterval = null;
        this.isChecking = false;
    }

    /**
     * Initialize version checking
     * Stores current version and starts periodic checks
     */
    init() {
        // Disable version checking in development mode to avoid constant notifications
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            logger.debug('[VersionChecker] Version checking disabled in development mode');
            return;
        }

        // Check if page was reloaded for update and show "Update Complete" message
        this.checkForUpdateComplete();

        // Store the initial version on first load
        this.currentVersion = this.getCurrentVersion();
        this.storeVersion(this.currentVersion);

        // Start periodic version checks
        this.startChecking();

        logger.debug('[VersionChecker] Initialized with version', { version: this.currentVersion });
    }

    /**
     * Check if page was reloaded after update and show completion message
     */
    checkForUpdateComplete() {
        try {
            const updateReloaded = window.sessionStorage.getItem('update-reloaded');
            if (updateReloaded === 'true') {
                // Clear the flag
                window.sessionStorage.removeItem('update-reloaded');
                // Show update complete notification
                this.showUpdateCompleteMessage();
            }
        } catch (error) {
            logger.warn('[VersionChecker] Error checking update-reloaded flag', { error });
        }
    }

    /**
     * Show "Update Complete" notification after successful update
     */
    showUpdateCompleteMessage() {
        const i18n = window.i18n;
        const title = i18n?.t('versionUpdate.complete', { ns: 'common', defaultValue: 'Update Complete' }) || 'Update Complete';
        const message = i18n?.t('versionUpdate.completeMessage', { ns: 'common', defaultValue: 'The application has been successfully updated with the latest features and improvements.' }) || 'The application has been successfully updated with the latest features and improvements.';

        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            padding: 16px 20px;
            border-radius: 12px;
            box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
            z-index: 10000;
            max-width: 400px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-size: 14px;
            line-height: 1.5;
            animation: slideInRight 0.3s ease-out;
        `;

        notification.innerHTML = `
            <div style="display: flex; align-items: center; gap: 12px;">
                <div style="
                    width: 24px;
                    height: 24px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                ">
                    ✓
                </div>
                <div>
                    <div style="font-weight: 600; margin-bottom: 4px;">${title}</div>
                    <div style="opacity: 0.9; font-size: 13px;">${message}</div>
                </div>
            </div>
        `;

        // Add animation styles
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
        `;

        document.head.appendChild(style);
        document.body.appendChild(notification);

        // Auto-remove after 5 seconds
        setTimeout(() => {
            if (notification.parentElement) {
                notification.style.animation = 'slideInRight 0.3s ease-out reverse';
                setTimeout(() => {
                    if (notification.parentElement) {
                        notification.remove();
                    }
                }, 300);
            }
        }, 5000);

        logger.info('[VersionChecker] Update complete notification shown');
    }

    /**
     * Get current app version from the main script tag
     * The hash in the filename changes with each build
     */
    getCurrentVersion() {
        const scripts = document.scripts;
        for (const script of scripts) {
            // Look for the main app script with hash in filename (production)
            // Vite generates alphanumeric hashes (a-z, A-Z, 0-9), so match all alphanumeric
            const prodMatch = script.src.match(/assets\/index\.([a-zA-Z0-9]+)\.js/);
            if (prodMatch) {
                return prodMatch[1]; // Return the hash as version
            }
            
            // In development, look for main.jsx and use a consistent timestamp
            const devMatch = script.src.match(/\/src\/main\.jsx/);
            if (devMatch) {
                // Use a fixed development version to avoid constant refresh notifications
                return 'dev-' + '20250204'; // Fixed date for development
            }
        }
        // Fallback: use timestamp if hash not found
        return document.documentElement.dataset.buildTime || Date.now().toString();
    }

    /**
     * Store version in localStorage
     */
    storeVersion(version) {
        try {
            localStorage.setItem(APP_VERSION_KEY, version);
        } catch (e) {
            logger.warn('[VersionChecker] Could not store version', { error: e });
        }
    }

    /**
     * Get stored version from localStorage
     */
    getStoredVersion() {
        try {
            return localStorage.getItem(APP_VERSION_KEY);
        } catch (e) {
            logger.warn('[VersionChecker] Could not get stored version', { error: e });
            return null;
        }
    }

    /**
     * Start periodic version checking
     */
    startChecking() {
        // Check immediately on visibility change (user returns to tab)
        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                this.checkVersion();
            }
        });

        // Periodic checks
        this.checkInterval = setInterval(() => {
            this.checkVersion();
        }, VERSION_CHECK_INTERVAL);
    }

    /**
     * Check if a new version is available
     */
    async checkVersion() {
        if (this.isChecking) {return;}
        this.isChecking = true;

        try {
            // Fetch index.html with cache-busting
            const response = await fetch(`/index.html?t=${Date.now()}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch version');
            }

            const html = await response.text();

            // Extract version from script tag in the fetched HTML
            // Vite generates alphanumeric hashes (a-z, A-Z, 0-9), so match all alphanumeric
            let newVersion = null;
            
            // Check for production build with hashed filename
            const prodMatch = html.match(/assets\/index\.([a-zA-Z0-9]+)\.js/);
            if (prodMatch) {
                newVersion = prodMatch[1];
            }
            
            // Check for development mode with main.jsx
            const devMatch = html.match(/\/src\/main\.jsx/);
            if (devMatch) {
                newVersion = 'dev-' + '20250204'; // Use same fixed date as getCurrentVersion
            }

            if (newVersion && newVersion !== this.currentVersion) {
                logger.debug('[VersionChecker] New version detected', { newVersion, currentVersion: this.currentVersion });
                const releaseNotes = await this.fetchReleaseNotes(newVersion);
                this.notifyNewVersion(newVersion, releaseNotes);
            }
        } catch (error) {
            logger.warn('[VersionChecker] Version check failed', { error });
        } finally {
            this.isChecking = false;
        }
    }

    async fetchReleaseNotes(newVersion) {
        try {
            const response = await fetch(`/UPDATENOTE/release-notes.json?t=${Date.now()}`, {
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache',
                    'Pragma': 'no-cache'
                }
            });

            if (!response.ok) {
                return null;
            }

            const payload = await response.json();
            if (!payload) {return null;}

            const releases = payload.releases || payload.releaseNotes || payload.notes || payload.data;
            if (Array.isArray(releases)) {
                const exactMatch = releases.find((release) => release?.version === newVersion);
                const latest = releases.find((release) => release?.version === 'latest') || releases[0];
                return exactMatch?.items || latest?.items || null;
            }

            if (Array.isArray(payload.items)) {
                return payload.items;
            }

            return null;
        } catch (error) {
            logger.warn('[VersionChecker] Release notes fetch failed', { error });
            return null;
        }
    }

    /**
     * Notify user about new version
     * Shows a non-intrusive notification with option to refresh
     */
    notifyNewVersion(newVersion, releaseNotes) {
        // Detect current theme
        const currentTheme = document.documentElement.getAttribute('data-theme') || 'light';
        const isDark = currentTheme === 'dark';

        const notesMarkup = Array.isArray(releaseNotes) && releaseNotes.length
            ? `
          <div class="version-update-features">
            ${releaseNotes.map((item) => `<span class="version-update-pill">${item}</span>`).join('')}
          </div>
        `
            : '';

        // Create notification element
        const notification = document.createElement('div');
        notification.className = 'version-update-notification';
        notification.innerHTML = `
      <div class="version-update-content">
        <div class="version-update-icon">
          <span class="material-symbols-outlined" style="font-size: 28px; display: block;">system_update</span>
        </div>
        <div class="version-update-text">
          <strong>${i18n.t('versionUpdate.title', { ns: 'common', defaultValue: 'Update ready' })}</strong>
          <p>${i18n.t('versionUpdate.message', { ns: 'common', defaultValue: "Hello! We've added the improvements below." })}</p>
          ${notesMarkup}
          <div class="version-update-actions">
            <button class="version-update-btn" onclick="location.reload(true)">
              ${i18n.t('versionUpdate.refreshButton', { ns: 'common', defaultValue: 'Refresh' })}
            </button>
          </div>
        </div>
        <button class="version-update-close" onclick="this.parentElement.parentElement.remove()" aria-label="${i18n.t('versionUpdate.closeLabel', { ns: 'common', defaultValue: 'Close notification' })}">
          <span class="material-symbols-outlined" style="font-size: 20px; display: block;">close</span>
        </button>
      </div>
    `;

        // Add styles using CSS variables and theme detection to match app theme
        const style = document.createElement('style');
        style.textContent = `
      .version-update-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 10000;
        max-width: 380px;
        animation: slideInRight 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }

      .version-update-content {
        background: var(--bg-card, ${isDark ? '#14171f' : '#ffffff'});
        color: var(--text-primary, ${isDark ? '#f1f5f9' : '#0f172a'});
        padding: 18px 22px 18px 18px;
        border-radius: 14px;
        box-shadow: var(--shadow-xl, 0 24px 30px -20px rgba(0, 0, 0, ${isDark ? '0.6' : '0.2'}));
        display: flex;
        align-items: flex-start;
        gap: 14px;
        position: relative;
        border: 1px solid var(--border-primary, ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'});
        backdrop-filter: blur(14px);
      }

      /* Dark mode specific styles */
      [data-theme="dark"] .version-update-content {
        background: var(--bg-card, #1a1a1a);
        color: var(--text-primary, #f1f5f9);
        border: 1px solid var(--border-primary, rgba(255, 255, 255, 0.1));
        box-shadow: var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3));
      }

      /* Light mode specific styles */
      [data-theme="light"] .version-update-content,
      :root:not([data-theme]) .version-update-content {
        background: var(--bg-card, #ffffff);
        color: var(--text-primary, #0f172a);
        border: 1px solid var(--border-primary, rgba(0, 0, 0, 0.1));
        box-shadow: var(--shadow-xl, 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04));
      }

      .version-update-icon {
        font-size: 24px;
        line-height: 1;
        margin-top: 1px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        border-radius: 10px;
        background: ${isDark ? 'rgba(59, 130, 246, 0.18)' : 'rgba(37, 99, 235, 0.12)'};
      }

      .version-update-icon .material-symbols-outlined {
        font-size: 22px !important;
        display: block;
        color: var(--text-primary, ${isDark ? '#f1f5f9' : '#0f172a'});
      }

      [data-theme="dark"] .version-update-icon {
        background: var(--bg-hover, rgba(255, 255, 255, 0.1));
      }

      [data-theme="light"] .version-update-icon,
      :root:not([data-theme]) .version-update-icon {
        background: var(--bg-hover, rgba(0, 0, 0, 0.05));
      }

      .version-update-text {
        flex: 1;
        min-width: 0;
        padding-right: 28px;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .version-update-text strong {
        display: block;
        font-size: 15px;
        font-weight: 600;
        margin-bottom: 0;
        color: var(--text-primary, ${isDark ? '#f1f5f9' : '#0f172a'});
        letter-spacing: -0.01em;
        line-height: 1.3;
      }

      .version-update-text p {
        margin: 0;
        font-size: 12.5px;
        line-height: 1.5;
        opacity: 0.9;
        color: var(--text-secondary, ${isDark ? '#cbd5e1' : '#475569'});
      }

      .version-update-features {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 2px;
      }

      .version-update-pill {
        font-size: 11px;
        font-weight: 600;
        padding: 5px 10px;
        border-radius: 999px;
        background: ${isDark ? 'rgba(96, 165, 250, 0.18)' : 'rgba(59, 130, 246, 0.12)'};
        color: ${isDark ? '#93c5fd' : '#1d4ed8'};
        border: 1px solid ${isDark ? 'rgba(96, 165, 250, 0.35)' : 'rgba(37, 99, 235, 0.18)'};
      }

      .version-update-actions {
        margin-top: 4px;
      }

      [data-theme="dark"] .version-update-text strong {
        color: var(--text-primary, #f1f5f9);
      }

      [data-theme="dark"] .version-update-text p {
        color: var(--text-secondary, #cbd5e1);
      }

      [data-theme="light"] .version-update-text strong,
      :root:not([data-theme]) .version-update-text strong {
        color: var(--text-primary, #0f172a);
      }

      [data-theme="light"] .version-update-text p,
      :root:not([data-theme]) .version-update-text p {
        color: var(--text-secondary, #475569);
      }

      .version-update-btn {
        background: var(--accent, #2563eb);
        color: var(--text-inverse, #ffffff);
        border: none;
        padding: 8px 14px;
        border-radius: 10px;
        font-weight: 600;
        font-size: 12px;
        cursor: pointer;
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        box-shadow: var(--accent-shadow, 0 4px 12px rgba(37, 99, 235, 0.35));
        align-self: flex-start;
      }

      .version-update-btn:hover {
        transform: translateY(-2px);
        box-shadow: var(--accent-shadow, 0 4px 12px rgba(37, 99, 235, 0.5));
        background: var(--accent-hover, ${isDark ? '#60a5fa' : '#1d4ed8'});
      }

      .version-update-btn:active {
        transform: translateY(0);
        box-shadow: var(--accent-shadow, 0 1px 4px rgba(37, 99, 235, 0.4));
      }

      [data-theme="dark"] .version-update-btn {
        background: var(--accent, #3b82f6);
        box-shadow: var(--accent-shadow, 0 2px 8px rgba(59, 130, 246, 0.4));
      }

      [data-theme="dark"] .version-update-btn:hover {
        background: var(--accent-hover, #60a5fa);
        box-shadow: var(--accent-shadow, 0 4px 12px rgba(59, 130, 246, 0.5));
      }

      .version-update-close {
        position: absolute;
        top: 12px;
        right: 12px;
        background: var(--bg-hover, ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'});
        border: none;
        color: var(--text-primary, ${isDark ? '#f1f5f9' : '#0f172a'});
        cursor: pointer;
        opacity: 0.7;
        transition: all 0.2s ease;
        padding: 0;
        line-height: 1;
        border-radius: 6px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10;
      }

      .version-update-close .material-symbols-outlined {
        font-size: 16px !important;
        display: block;
        color: var(--text-primary, ${isDark ? '#f1f5f9' : '#0f172a'});
      }

      .version-update-close:hover {
        opacity: 1;
        background: var(--bg-hover, ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'});
        transform: scale(1.1);
      }

      [data-theme="dark"] .version-update-close {
        background: var(--bg-hover, rgba(255, 255, 255, 0.1));
        color: var(--text-primary, #f1f5f9);
      }

      [data-theme="dark"] .version-update-close:hover {
        background: var(--bg-hover, rgba(255, 255, 255, 0.2));
      }

      [data-theme="light"] .version-update-close,
      :root:not([data-theme]) .version-update-close {
        background: var(--bg-hover, rgba(0, 0, 0, 0.05));
        color: var(--text-primary, #0f172a);
      }

      [data-theme="light"] .version-update-close:hover,
      :root:not([data-theme]) .version-update-close:hover {
        background: var(--bg-hover, rgba(0, 0, 0, 0.1));
      }

      @keyframes slideInRight {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      @media (max-width: 768px) {
        .version-update-notification {
          top: 10px;
          right: 10px;
          left: 10px;
          max-width: none;
        }

        .version-update-content {
          flex-direction: column;
          align-items: stretch;
          padding: 18px 50px 18px 20px;
          gap: 12px;
        }

        .version-update-btn {
          width: 100%;
          justify-content: center;
        }

        .version-update-icon {
          margin-top: 0;
        }
      }

      @media (max-width: 480px) {
        .version-update-notification {
          top: 8px;
          right: 8px;
          left: 8px;
        }

        .version-update-content {
          padding: 16px;
        }

        .version-update-text strong {
          font-size: 15px;
        }

        .version-update-text p {
          font-size: 13px;
        }

        .version-update-btn {
          padding: 9px 18px;
          font-size: 13px;
        }
      }
    `;

        document.head.appendChild(style);
        document.body.appendChild(notification);

        // Auto-remove after 30 seconds if user doesn't interact
        setTimeout(() => {
            if (notification.parentElement) {
                notification.remove();
            }
        }, 30000);

        // Update stored version
        this.storeVersion(newVersion);
    }

    /**
     * Stop version checking (cleanup)
     */
    stop() {
        if (this.checkInterval) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }

    /**
     * Manually trigger notification (for testing)
     */
    testNotification() {
        logger.debug('[VersionChecker] Manual test notification triggered');
        this.notifyNewVersion('test-version-' + Date.now());
    }

    /**
     * Manually check for updates and show notification if available
     * This can be called from other parts of the app (e.g., lazyWithRetry)
     */
    async checkForUpdates() {
        logger.debug('[VersionChecker] Manual update check triggered');
        try {
            const newVersion = await this.fetchLatestVersion();
            if (newVersion && newVersion !== this.currentVersion) {
                logger.info('[VersionChecker] New version detected via manual check', { 
                    current: this.currentVersion, 
                    new: newVersion 
                });
                this.notifyNewVersion(newVersion);
            } else {
                logger.debug('[VersionChecker] No new version found in manual check');
                // Even if no new version is detected, show update notification for chunk loading errors
                // This provides a better UX than the "Something went wrong" popup
                this.notifyNewVersion('update-available');
            }
        } catch (error) {
            logger.error('[VersionChecker] Error in manual update check', { error });
            // Show update notification even on error to provide better UX
            this.notifyNewVersion('update-available');
        }
    }
}

// Export singleton instance
export const versionChecker = new VersionChecker();

// Expose to window for testing in browser console
if (typeof window !== 'undefined') {
    window.testVersionNotification = () => {
        versionChecker.testNotification();
    };
    
    // Also expose manual update check
    window.checkForUpdates = () => {
        versionChecker.checkForUpdates();
    };
}

// Auto-initialize when module is imported
if (typeof window !== 'undefined') {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            versionChecker.init();
        });
    } else {
        versionChecker.init();
    }
}
